import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowDown from '@/assets/icons/arrow-down.svg?react';
import CheckMark from '@/assets/icons/check-mark.svg?react';
import Globe from '@/assets/icons/globe.svg?react';
import { changeLanguage } from '@/i18n/utils';

import styles from './index.module.scss';

const storageKey = 'i18nextLogtoUiLng';
const languageLabel = 'Language / 语言 / 語言';

const languages = [
  { value: 'en', label: 'English' },
  { value: 'zh-CN', label: '中文' },
  { value: 'zh-TW', label: '繁體中文' },
] as const;

type LanguageValue = (typeof languages)[number]['value'];

const getLanguageValue = (language: string): LanguageValue => {
  const normalizedLanguage = language.toLowerCase();

  if (normalizedLanguage.startsWith('zh-tw') || normalizedLanguage.startsWith('zh-hk')) {
    return 'zh-TW';
  }

  if (normalizedLanguage.startsWith('zh')) {
    return 'zh-CN';
  }

  return 'en';
};

const LanguageSelector = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentLanguage = getLanguageValue(language);
  const currentLabel = languages.find(({ value }) => value === currentLanguage)?.label ?? 'English';

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = ({ target }: MouseEvent) => {
      if (target instanceof Node && dropdownRef.current?.contains(target)) {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = async (selectedLanguage: LanguageValue) => {
    if (isChanging || selectedLanguage === currentLanguage) {
      setIsOpen(false);
      return;
    }

    setIsChanging(true);

    try {
      await changeLanguage(selectedLanguage);
      localStorage.setItem(storageKey, selectedLanguage);
      setIsOpen(false);
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <div ref={dropdownRef} className={styles.dropdown}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={languageLabel}
        className={styles.trigger}
        disabled={isChanging}
        title={currentLabel}
        onClick={() => {
          setIsOpen((previous) => !previous);
        }}
      >
        <Globe aria-hidden className={styles.globe} />
        <span className={styles.label}>{currentLabel}</span>
        <ArrowDown
          aria-hidden
          className={classNames(styles.chevron, isOpen && styles.chevronOpen)}
        />
      </button>
      {isOpen && (
        <div className={styles.menu} role="menu" aria-label={languageLabel}>
          {languages.map(({ value, label }) => {
            const isCurrent = value === currentLanguage;

            return (
              <button
                key={value}
                type="button"
                role="menuitem"
                aria-current={isCurrent ? true : undefined}
                className={classNames(styles.item, isCurrent && styles.itemCurrent)}
                disabled={isChanging}
                onClick={() => {
                  void handleSelect(value);
                }}
              >
                <span>{label}</span>
                {isCurrent && <CheckMark aria-hidden className={styles.check} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
